import { t } from '../i18n';
import { ChatModel, type ChatModelChange } from './ChatModel';
import { CHAT_MESSAGE_MAX_BYTES, CHAT_MESSAGE_MAX_CHARACTERS, type ChatMessage } from './ChatTypes';
import './chat.css';

export const CHAT_RECENT_VISIBILITY_MS = 8_000;
const MATCH_HISTORY_RELEASE_HOLD_MS = 3_000;
const MATCH_HISTORY_FADE_OUT_MS = 150;

export interface ChatPanelLabels {
  readonly title: string;
  readonly placeholder: string;
  readonly send: string;
  readonly close: string;
  readonly collapse: string;
  readonly sendFailed: string;
  readonly messageTooLong: string;
  readonly system: string;
  readonly guest: string;
  readonly account: string;
  readonly bot: string;
}

export interface ChatMessagePresentation {
  readonly authorLabel?: string;
  readonly authorColor?: string;
}

export interface ChatPanelOptions {
  readonly model: ChatModel;
  readonly placement: 'bottom-left' | 'top-left' | 'top-right';
  /** Bottom-left starts expanded; top placements show only recent arrivals until expanded. */
  readonly expanded?: boolean;
  readonly composerMode?: 'on-demand' | 'persistent';
  readonly variant?: 'default' | 'home' | 'match';
  readonly labels?: Partial<ChatPanelLabels>;
  readonly messagePresentation?: (message: ChatMessage) => ChatMessagePresentation;
  readonly submitMode?: 'await-acceptance' | 'close-immediately';
  /** Resolve after accepted submission; reject to retain the draft and display a retryable error. */
  readonly onSend: (text: string) => void | Promise<void>;
  readonly onSendAccepted?: () => void;
  readonly onTextInputActiveChange?: (active: boolean) => void;
}

const defaultLabels = (): ChatPanelLabels => ({
  title: t('chat:panel.title'),
  placeholder: t('chat:panel.placeholder'),
  send: t('chat:panel.send'),
  close: t('chat:panel.close'),
  collapse: t('chat:panel.collapse'),
  sendFailed: t('chat:panel.sendFailed'),
  messageTooLong: t('chat:panel.messageTooLong', { count: CHAT_MESSAGE_MAX_CHARACTERS }),
  system: t('chat:panel.system'),
  guest: t('chat:panel.guest'),
  account: t('chat:panel.account'),
  bot: t('chat:panel.bot'),
});

/** DOM presentation only. The owner appends element and routes semantic actions to this view. */
export class ChatPanel {
  readonly element = document.createElement('section');
  readonly #header = document.createElement('header');
  readonly #log = document.createElement('ol');
  readonly #form = document.createElement('form');
  readonly #input = document.createElement('input');
  readonly #send = document.createElement('button');
  readonly #close = document.createElement('button');
  readonly #collapse = document.createElement('button');
  readonly #open = document.createElement('button');
  readonly #status = document.createElement('p');
  readonly #rows = new Map<string, HTMLLIElement>();
  readonly #recentUntil = new Map<string, number>();
  readonly #options: ChatPanelOptions;
  readonly #labels: ChatPanelLabels;
  readonly #unsubscribe: () => void;
  readonly #persistentComposer: boolean;
  readonly #matchVariant: boolean;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #historyDismissTimer: ReturnType<typeof setTimeout> | undefined;
  #historyFadeTimer: ReturnType<typeof setTimeout> | undefined;
  #expanded: boolean;
  #historyVisible = false;
  #historyHeld = false;
  #composerOpen = false;
  #composing = false;
  #sending = false;
  #sendEnabled = true;
  #visible = true;
  #disposed = false;
  #composerVersion = 0;

  constructor(options: ChatPanelOptions) {
    this.#options = options;
    this.#labels = { ...defaultLabels(), ...options.labels };
    this.#persistentComposer = options.composerMode === 'persistent';
    this.#matchVariant = options.variant === 'match';
    this.#expanded = options.expanded ?? options.placement === 'bottom-left';
    this.element.className = `chat-panel chat-panel--${options.placement} chat-panel--${options.variant ?? 'default'}`;
    this.element.setAttribute('aria-label', this.#labels.title);
    this.#header.className = 'chat-panel__header';
    this.#open.className = 'chat-panel__open';
    this.#open.type = 'button';
    this.#open.textContent = this.#labels.title;
    this.#open.setAttribute('aria-expanded', 'false');
    this.#collapse.className = 'chat-panel__collapse';
    this.#collapse.type = 'button';
    this.#collapse.textContent = this.#labels.collapse;
    this.#collapse.setAttribute('aria-label', this.#labels.collapse);
    this.#log.className = 'chat-panel__log';
    this.#log.setAttribute('role', 'log');
    this.#log.setAttribute('aria-live', 'polite');
    this.#log.setAttribute('aria-relevant', 'additions');
    this.#log.setAttribute('aria-label', this.#labels.title);
    this.#log.tabIndex = 0;
    this.#form.className = 'chat-panel__composer';
    this.#form.hidden = true;
    this.#input.className = 'chat-panel__input';
    this.#input.type = 'text';
    this.#input.placeholder = this.#labels.placeholder;
    this.#input.setAttribute('aria-label', this.#labels.placeholder);
    this.#input.autocomplete = 'off';
    // UTF-16 can use two code units per character. Submission validates Unicode code points.
    this.#input.maxLength = CHAT_MESSAGE_MAX_CHARACTERS * 2;
    this.#send.type = 'submit';
    this.#send.textContent = this.#labels.send;
    this.#close.type = 'button';
    this.#close.textContent = this.#labels.close;
    this.#close.hidden = this.#persistentComposer;
    this.#status.className = 'chat-panel__status';
    this.#status.setAttribute('role', 'status');
    this.#status.hidden = true;
    this.#form.append(this.#input, this.#send, this.#close);
    this.#header.append(this.#open, this.#collapse);
    this.element.append(this.#header, this.#log, this.#form, this.#status);
    this.#open.addEventListener('click', this.#onOpen);
    this.#close.addEventListener('click', this.#onClose);
    this.#collapse.addEventListener('click', this.#onCollapse);
    this.#form.addEventListener('submit', this.#onSubmit);
    this.element.addEventListener('keydown', this.#onKeyDown);
    this.element.addEventListener('focusout', this.#onFocusOut);
    this.#input.addEventListener('compositionstart', this.#onCompositionStart);
    this.#input.addEventListener('compositionend', this.#onCompositionEnd);
    this.#input.addEventListener('focus', this.#onInputFocus);
    window.addEventListener('blur', this.#onWindowBlur);
    this.#unsubscribe = options.model.subscribe(this.#onModelChange);
    this.#render();
  }

  get composerOpen(): boolean {
    return this.#composerOpen;
  }

  openComposer(): void {
    if (this.#disposed || !this.#sendEnabled) return;
    this.#cancelHistoryFade();
    if (!this.#historyHeld) this.#historyVisible = false;
    if (this.#persistentComposer && !this.#expanded) this.#expanded = true;
    if (!this.#composerOpen) {
      this.#composerOpen = true;
      this.#composerVersion++;
      this.#options.onTextInputActiveChange?.(true);
      this.#render();
    }
    this.#input.focus({ preventScroll: true });
    this.#log.scrollTop = this.#log.scrollHeight;
  }

  /** Keeps the draft for reopening. Focus and pointer lock are never restored without a gesture. */
  closeComposer(): void {
    if (!this.#composerOpen) return;
    this.#composerOpen = false;
    this.#composerVersion++;
    this.#composing = false;
    if (this.element.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    this.#options.onTextInputActiveChange?.(false);
    this.#render();
  }

  /** Reveals retained history without focusing or activating text input. */
  setHistoryVisible(visible: boolean): void {
    if (this.#disposed || this.#historyHeld === visible) return;
    this.#historyHeld = visible;
    this.#cancelHistoryFade();
    if (visible || !this.#matchVariant) {
      this.#historyVisible = visible;
      this.#render();
      if (visible) this.#log.scrollTop = this.#log.scrollHeight;
      return;
    }
    this.#scheduleHistoryFade(MATCH_HISTORY_RELEASE_HOLD_MS);
  }

  #scheduleHistoryFade(delayMs: number): void {
    this.#historyDismissTimer = setTimeout(() => {
      this.#historyDismissTimer = undefined;
      if (this.#composerOpen) {
        this.#historyVisible = false;
        this.#render();
        return;
      }
      const now = performance.now();
      let latestRecentExpiry = 0;
      for (const expiry of this.#recentUntil.values()) latestRecentExpiry = Math.max(latestRecentExpiry, expiry);
      if (latestRecentExpiry > now) {
        this.#scheduleHistoryFade(latestRecentExpiry - now);
        return;
      }
      this.element.classList.add('chat-panel--history-exiting');
      this.#historyFadeTimer = setTimeout(() => {
        this.#historyFadeTimer = undefined;
        this.#historyVisible = false;
        this.#render();
        this.element.classList.remove('chat-panel--history-exiting');
      }, MATCH_HISTORY_FADE_OUT_MS);
    }, delayMs);
  }

  #cancelHistoryFade(): void {
    clearTimeout(this.#historyDismissTimer);
    clearTimeout(this.#historyFadeTimer);
    this.#historyDismissTimer = undefined;
    this.#historyFadeTimer = undefined;
    this.element.classList.remove('chat-panel--history-exiting');
  }

  setExpanded(expanded: boolean): void {
    if (this.#disposed || this.#expanded === expanded) return;
    if (!expanded && this.#persistentComposer) this.closeComposer();
    this.#expanded = expanded;
    this.#render();
    if (expanded) this.#log.scrollTop = this.#log.scrollHeight;
  }

  setSendEnabled(enabled: boolean, reason = ''): void {
    if (this.#disposed) return;
    this.#sendEnabled = enabled;
    this.#setStatus(enabled ? '' : reason);
    if (!enabled) this.closeComposer();
    this.#render();
  }

  /** Allows channel policy/lifecycle to hide the whole surface without destroying its history. */
  setVisible(visible: boolean): void {
    if (this.#disposed || this.#visible === visible) return;
    this.#visible = visible;
    if (!visible) this.closeComposer();
    this.#render();
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#cancelHistoryFade();
    this.closeComposer();
    this.#disposed = true;
    clearTimeout(this.#timer);
    this.#unsubscribe();
    this.#open.removeEventListener('click', this.#onOpen);
    this.#close.removeEventListener('click', this.#onClose);
    this.#collapse.removeEventListener('click', this.#onCollapse);
    this.#form.removeEventListener('submit', this.#onSubmit);
    this.element.removeEventListener('keydown', this.#onKeyDown);
    this.element.removeEventListener('focusout', this.#onFocusOut);
    this.#input.removeEventListener('compositionstart', this.#onCompositionStart);
    this.#input.removeEventListener('compositionend', this.#onCompositionEnd);
    this.#input.removeEventListener('focus', this.#onInputFocus);
    window.removeEventListener('blur', this.#onWindowBlur);
    this.#rows.clear();
    this.#recentUntil.clear();
    this.element.remove();
  }

  #onOpen = (): void => {
    if (this.#persistentComposer && !this.#expanded) {
      this.setExpanded(true);
      return;
    }
    this.openComposer();
  };
  #onClose = (): void => { this.closeComposer(); };
  #onCollapse = (): void => { this.setExpanded(false); };
  #onWindowBlur = (): void => { this.closeComposer(); };
  #onCompositionStart = (): void => { this.#composing = true; };
  #onCompositionEnd = (): void => { this.#composing = false; };
  #onInputFocus = (): void => {
    if (this.#persistentComposer && this.#sendEnabled && !this.#composerOpen) this.openComposer();
  };
  #onFocusOut = (event: FocusEvent): void => {
    if (!(event.relatedTarget instanceof Node) || !this.element.contains(event.relatedTarget)) this.closeComposer();
  };

  #onKeyDown = (event: KeyboardEvent): void => {
    if (this.#persistentComposer) {
      if (!this.#expanded || event.target !== this.#input) return;
    } else if (!this.#composerOpen) return;
    event.stopPropagation();
    // IME Enter/Escape belongs to candidate selection, including browsers reporting keyCode 229.
    if (event.isComposing || this.#composing || event.keyCode === 229) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeComposer();
    } else if (event.key === 'Enter' && event.target === this.#input) {
      event.preventDefault();
      if (!event.repeat) void this.#submit();
    }
  };

  #onSubmit = (event: SubmitEvent): void => {
    event.preventDefault();
    if (!this.#composing) void this.#submit();
  };

  async #submit(): Promise<void> {
    const composerAvailable = this.#persistentComposer ? this.#expanded : this.#composerOpen;
    if (this.#disposed || !composerAvailable || !this.#sendEnabled || this.#sending) return;
    const text = this.#input.value.trim();
    const closeImmediately = this.#options.submitMode === 'close-immediately' && !this.#persistentComposer;
    if (!text) {
      if (closeImmediately) this.closeComposer();
      return;
    }
    if (Array.from(text).length > CHAT_MESSAGE_MAX_CHARACTERS || new TextEncoder().encode(text).length > CHAT_MESSAGE_MAX_BYTES) {
      this.#setStatus(this.#labels.messageTooLong);
      return;
    }
    const version = this.#composerVersion;
    this.#sending = true;
    this.#setStatus('');
    if (closeImmediately) this.closeComposer();
    else this.#render();
    try {
      await this.#options.onSend(text);
      if (this.#disposed) return;
      try { this.#options.onSendAccepted?.(); } catch { /* Post-send feedback cannot invalidate an accepted message. */ }
      this.#input.value = '';
      if (!closeImmediately && !this.#persistentComposer && version === this.#composerVersion) this.closeComposer();
    } catch {
      if (!this.#disposed && this.#sendEnabled) this.#setStatus(this.#labels.sendFailed);
    } finally {
      this.#sending = false;
      if (!this.#disposed) this.#render();
    }
  }

  #onModelChange = (change: ChatModelChange): void => {
    const follow = this.#log.scrollHeight - this.#log.scrollTop - this.#log.clientHeight < 32;
    if (change.kind === 'message') this.#recentUntil.set(change.message.id, performance.now() + CHAT_RECENT_VISIBILITY_MS);
    if (change.kind === 'message' && this.#historyFadeTimer) {
      this.#cancelHistoryFade();
      this.#scheduleHistoryFade(CHAT_RECENT_VISIBILITY_MS);
    }
    if (change.kind === 'clear') this.#recentUntil.clear();
    this.#render();
    if (follow || !(this.#expanded || this.#composerOpen)) this.#log.scrollTop = this.#log.scrollHeight;
  };

  #render(): void {
    clearTimeout(this.#timer);
    const now = performance.now();
    const expanded = this.#expanded || (!this.#persistentComposer && this.#composerOpen);
    const composerVisible = this.#persistentComposer ? this.#expanded : this.#composerOpen;
    const messages = this.#options.model.messages;
    const retained = new Set(messages.map((message) => message.id));
    for (const [id, row] of this.#rows) {
      if (retained.has(id)) continue;
      row.remove();
      this.#rows.delete(id);
      this.#recentUntil.delete(id);
    }
    let hasRecent = false;
    let nextExpiry = Infinity;
    for (const [index, message] of messages.entries()) {
      let row = this.#rows.get(message.id);
      if (!row) {
        row = this.#createRow(message);
        this.#rows.set(message.id, row);
      }
      if (this.#log.children[index] !== row) this.#log.insertBefore(row, this.#log.children[index] ?? null);
      const expiry = this.#recentUntil.get(message.id) ?? 0;
      const recent = expiry > now;
      if (recent) {
        hasRecent = true;
        nextExpiry = Math.min(nextExpiry, expiry);
      } else this.#recentUntil.delete(message.id);
      row.hidden = this.#persistentComposer ? !expanded : !expanded && !this.#historyVisible && !recent;
    }
    this.element.hidden = !this.#visible
      || (!this.#persistentComposer && !expanded && !this.#historyVisible && !hasRecent);
    this.element.classList.toggle('chat-panel--expanded', expanded);
    this.element.classList.toggle('chat-panel--collapsed', this.#persistentComposer && !this.#expanded);
    this.element.classList.toggle('chat-panel--persistent', this.#persistentComposer);
    this.#header.hidden = this.#matchVariant || (!this.#persistentComposer && !expanded);
    this.#open.hidden = this.#matchVariant || (!this.#persistentComposer && !expanded);
    this.#open.disabled = !this.#persistentComposer && !this.#sendEnabled;
    this.#open.setAttribute('aria-expanded', String(this.#persistentComposer ? this.#expanded : this.#composerOpen));
    this.#collapse.hidden = !this.#persistentComposer || !this.#expanded;
    this.#log.hidden = this.#persistentComposer && !this.#expanded;
    this.#log.tabIndex = expanded ? 0 : -1;
    this.#form.hidden = !composerVisible;
    this.#input.disabled = !this.#sendEnabled;
    this.#input.readOnly = this.#sending;
    this.#send.disabled = this.#sending || !this.#sendEnabled;
    this.#form.setAttribute('aria-busy', String(this.#sending));
    if (Number.isFinite(nextExpiry)) this.#timer = setTimeout(() => this.#render(), Math.max(1, nextExpiry - now));
  }

  #createRow(message: ChatMessage): HTMLLIElement {
    const row = document.createElement('li');
    row.className = `chat-panel__message chat-panel__message--${message.kind}`;
    const text = document.createElement('span');
    text.className = 'chat-panel__text';
    text.textContent = message.text;
    if (this.#matchVariant && message.kind === 'system') {
      row.append(text);
      return row;
    }
    const author = document.createElement('span');
    author.className = 'chat-panel__author';
    const presentation = this.#options.messagePresentation?.(message);
    author.textContent = presentation?.authorLabel ?? message.author?.displayName ?? this.#labels.system;
    if (presentation?.authorColor && /^#[0-9a-fA-F]{6}$/.test(presentation.authorColor)) {
      author.style.setProperty('--chat-author-color', presentation.authorColor);
    }
    if (this.#matchVariant) {
      const separator = document.createElement('span');
      separator.className = 'chat-panel__separator';
      separator.textContent = ': ';
      row.append(author, separator, text);
    } else {
      const kind = document.createElement('span');
      kind.className = 'chat-panel__kind';
      kind.textContent = message.author ? this.#labels[message.author.kind] : this.#labels.system;
      row.append(author, kind, text);
    }
    return row;
  }

  #setStatus(text: string): void {
    this.#status.textContent = text;
    this.#status.hidden = !text;
  }
}
